import type { ReactNode } from 'react';
import {
  ART_WINDOW,
  CARD_BODY,
  CARD_SHADOW,
  FEET_SHADOW,
  fitFigure,
  GIANT_NUMBER,
  POPOUT_CLIP,
  ZONES,
} from '#shared/core/card-geometry';
import { type TierId, tierById, UNRATED_FILL } from '#shared/core/ratings';
import type { PortraitNudge, StudioPreset } from '#shared/schemas/media';
import { TIER_CONTENT_Y, TierShapeSvg } from '@/ui';
import type { CardSize, FigureArt } from './art';
import { Backdrop, INK, INK_SOFT, numeralFill } from './backdrops';
import { MANNEQUIN_LAYOUT, Mannequin } from './mannequin';
import type { AttrView } from './model';
import type { RevealFlags } from './reveal';
import { BAR_STAGGER_MS } from './reveal';
import { fitName, fitSize, textWidth } from './text';

/**
 * The player card as one SVG on the 500 x 700 board of CARD_GEOMETRY. Pure: everything it draws
 * comes in through props, so PlayerCard (on screen) and the PNG export (static markup) render
 * the same thing. Colors are literal hex and text uses presentation attributes, because the
 * export rasterizes a serialized copy that has no stylesheet.
 *
 * Draw order (add-fullbody.md 3.3): card shadow, body, window fill and backdrop, giant numeral,
 * window border, feet shadow, figure in the pop-out clip, OVR, tier and ribbon, nameplate,
 * attributes.
 *
 * Horizontal centering: everything sits on x 242, the middle of the card body (4..480), with the
 * shadow filling the rest of the 500 px board. The numeral is skewed around its own middle so the
 * skew does not push it off center.
 */

export const FONT = "'Fira Sans', 'Arial Black', 'Helvetica Neue', sans-serif";
const WHITE = '#ffffff';
const PALE = '#edd0e4';
const SKEW = -7;
const TAN = Math.tan((-SKEW * Math.PI) / 180);
/** Cap height of Fira Sans, as a share of the font size. */
const CAP = 0.69;

export interface CardSvgProps {
  size: CardSize;
  /** Unique per card on the page: clip paths and the silhouette filter hang their ids on it. */
  uid: string;
  title: string;
  name: string;
  jersey: string | null;
  /** The number the big numerals print. null prints ??. */
  shownOvr: number | null;
  tier: TierId | null;
  tierTag: string;
  /** PROVISIONAL or RUSTY, null for none. */
  stateTag: string | null;
  /** The short form for the medium card. */
  stateTagShort: string | null;
  /** Flat fill of the state tag: butter for provisional, peach for rusty. */
  stateFill: string;
  ribbon: string;
  subline: string;
  attrs: AttrView[];
  preset: StudioPreset;
  figure: FigureArt;
  nudge: PortraitNudge | null;
  flags: RevealFlags;
  /** Hard shadow color: a CSS var on screen, the grape hex in the export. */
  shadowColor: string;
  /** Play the little entrance animations (stamp, pop, bars). Off in the export. */
  animate: boolean;
  singleDigitTitle: string;
}

/**
 * Transform of the giant numeral: origin at its baseline center, skewed around the middle of the
 * digits (not the baseline), so the skew does not push the numeral sideways. A skew about the
 * baseline would move the middle of 300 px digits by about 13 px (add-fullbody.md did not account
 * for it; docs/changes/cards-geometry.md).
 */
export function numeralTransform(cx: number, baseline: number, fontSize: number, skewDeg: number): string {
  const half = round(fontSize * GIANT_NUMBER.midShare);
  return `translate(${cx} ${baseline}) translate(0 ${-half}) skewX(${skewDeg}) translate(0 ${half})`;
}

const round = (n: number) => Math.round(n * 100) / 100;

function T({
  x,
  y,
  size,
  anchor = 'start',
  fill = INK,
  weight = 900,
  italic = true,
  stroke,
  strokeWidth,
  opacity,
  className,
  style,
  children,
}: {
  x: number;
  y: number;
  size: number;
  anchor?: 'start' | 'middle' | 'end';
  fill?: string;
  weight?: number;
  italic?: boolean;
  stroke?: string;
  strokeWidth?: number;
  opacity?: number;
  className?: string;
  style?: React.CSSProperties;
  children: ReactNode;
}) {
  return (
    <text
      x={round(x)}
      y={round(y)}
      fontFamily={FONT}
      fontWeight={weight}
      fontStyle={italic ? 'italic' : 'normal'}
      fontSize={round(size)}
      textAnchor={anchor}
      fill={fill}
      stroke={stroke}
      strokeWidth={strokeWidth}
      strokeLinejoin={stroke ? 'round' : undefined}
      paintOrder={stroke ? 'stroke' : undefined}
      opacity={opacity}
      className={className}
      style={{ fontVariantNumeric: 'tabular-nums', ...style }}
    >
      {children}
    </text>
  );
}

/** A slanted plate with an ink outline: x, y, w, h are the unskewed box, the skew pivots on its middle. */
function Plate({
  x,
  y,
  w,
  h,
  fill,
  stroke = 4,
  className,
  children,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  fill: string;
  stroke?: number;
  className?: string;
  children?: ReactNode;
}) {
  const cx = x + w / 2;
  const cy = y + h / 2;
  return (
    <g className={className}>
      <rect
        x={round(x)}
        y={round(y)}
        width={round(w)}
        height={h}
        rx={3}
        fill={fill}
        stroke={INK}
        strokeWidth={stroke}
        strokeLinejoin="round"
        transform={`translate(${round(cx)} ${round(cy)}) skewX(${SKEW}) translate(${-round(cx)} ${-round(cy)})`}
      />
      {children}
    </g>
  );
}

/**
 * The no-cutout art is a 2:3 figure on a flat backdrop and the window is 444 x 476, so it is drawn
 * whole (meet), standing on the window floor. Slice would fill the width and crop about 14 percent
 * off the head and the feet (add-fullbody.md: "the art inside the window with a flat tier panel
 * behind it"). The tier fill on both sides of the art is the panel.
 */
export const WINDOW_ART_FIT = 'xMidYMax meet';

/** Font size inside each tier shape, as a share of the shape size, so two digits stay inside the outline. */
const NUM_FACTOR = { circle: 0.56, square: 0.6, shield: 0.55, hexagon: 0.57, diamond: 0.45, star8: 0.42 } as const;
/**
 * The chip is 72 px wide, so its badge is drawn big and the shapes with little room inside get a
 * larger number than on the L card (two digits still fit, the S chip has nothing else to print).
 */
const NUM_FACTOR_CHIP = { circle: 0.58, square: 0.62, shield: 0.56, hexagon: 0.58, diamond: 0.5, star8: 0.48 } as const;
/** Badge box on the S chip, in card units: centered, sitting on the bottom edge of the card body. */
export const CHIP_BADGE_BOX = 280;

function shapeKey(tier: TierId | null): keyof typeof NUM_FACTOR | 'unrated' {
  return tier ? tierById(tier).shape : 'unrated';
}

/** A tier shape with the OVR number centered in it. `box` is the square the shape and its shadow must fit. */
function OvrBadge({
  tier,
  value,
  x,
  y,
  box,
  shadowColor,
  animate,
  stamp,
  chip = false,
}: {
  tier: TierId | null;
  value: number | null;
  x: number;
  y: number;
  box: number;
  shadowColor: string;
  animate: boolean;
  stamp: boolean;
  /** The S chip: bigger number factors. */
  chip?: boolean;
}) {
  const myth = tier === 'myth';
  const stackStep = 5;
  const shadow = 8;
  const pad = tier === null ? shadow : myth ? stackStep * 4 : shadow;
  const size = box - pad;
  const key = shapeKey(tier);
  const factors = chip ? NUM_FACTOR_CHIP : NUM_FACTOR;
  const font = size * (key === 'unrated' ? 0.56 : factors[key]);
  const cx = x + size / 2;
  const cy = y + size * TIER_CONTENT_Y[key === 'unrated' ? 'unrated' : key] + (font * CAP) / 2;
  const text = value === null ? '??' : String(value);
  return (
    <g className={stamp && animate ? 'tts-cards-stamp' : undefined}>
      <TierShapeSvg
        tier={tier}
        x={x}
        y={y}
        size={size}
        strokeWidth={6}
        shadowOffset={shadow}
        stackStep={stackStep}
        shadowColor={shadowColor}
        animate={animate}
      />
      <T x={cx} y={cy} size={font} anchor="middle">
        {text}
      </T>
    </g>
  );
}

function Snowflake({ x, y, title }: { x: number; y: number; title: string }) {
  const arms = [0, 60, 120].map((deg) => {
    const a = (deg * Math.PI) / 180;
    const dx = Math.cos(a) * 8;
    const dy = Math.sin(a) * 8;
    return `M${round(x - dx)},${round(y - dy)}L${round(x + dx)},${round(y + dy)}`;
  });
  return (
    <g>
      <title>{title}</title>
      <path d={arms.join('')} stroke={INK} strokeWidth={6} strokeLinecap="round" fill="none" />
      <path d={arms.join('')} stroke="#97f0ff" strokeWidth={2.6} strokeLinecap="round" fill="none" />
    </g>
  );
}

function windowFill(tier: TierId | null): string {
  return tier ? tierById(tier).fill : UNRATED_FILL;
}

/** Layout of the six-attribute grid (L) and the top-three row (M), in card coordinates. */
function AttrBlock({
  attrs,
  size,
  tier,
  animate,
  singleDigitTitle,
}: {
  attrs: AttrView[];
  size: 'l' | 'm';
  tier: TierId | null;
  animate: boolean;
  singleDigitTitle: string;
}) {
  const [zx, zy, zw] = ZONES.attrs;
  const cols = 3;
  const cw = zw / cols;
  const fillColor = windowFill(tier);
  return (
    <g>
      {attrs.map((a, i) => {
        const col = i % cols;
        const row = Math.floor(i / cols);
        const x = zx + col * cw + 6;
        const w = cw - 12;
        const unknown = a.value === null;
        if (size === 'm') {
          return (
            <g key={a.key} opacity={unknown ? 0.6 : 1}>
              <T x={x + w / 2} y={zy + 26} size={24} anchor="middle" fill={INK_SOFT}>
                {a.label}
              </T>
              <T x={x + w / 2} y={zy + 76} size={a.value === null ? 40 : 48} anchor="middle">
                {a.value === null ? '??' : a.value}
              </T>
            </g>
          );
        }
        const y = zy + row * 44;
        const barY = y + 27;
        const bw = w;
        const bf = Math.max(a.fraction > 0 ? 7 : 0, a.fraction * bw);
        return (
          <g key={a.key}>
            <T x={x + 2} y={y + 21} size={17} fill={INK_SOFT} opacity={unknown ? 0.6 : 1}>
              {a.label}
            </T>
            {a.singleDigit && <Snowflake x={x + w - 56} y={y + 14} title={singleDigitTitle} />}
            <T
              x={x + w - 2}
              y={y + 24}
              size={28}
              anchor="end"
              fill={unknown ? INK_SOFT : INK}
              opacity={unknown ? 0.6 : 1}
            >
              {a.value === null ? '??' : a.value}
            </T>
            <rect x={x} y={barY} width={bw} height={12} rx={3} fill={PALE} stroke={INK} strokeWidth={3} />
            {bf > 0 && (
              <rect
                x={x}
                y={barY}
                width={round(bf)}
                height={12}
                rx={3}
                fill={fillColor}
                stroke={INK}
                strokeWidth={3}
                className={animate ? 'tts-cards-bar' : undefined}
                style={animate ? { animationDelay: `${i * BAR_STAGGER_MS}ms` } : undefined}
              />
            )}
          </g>
        );
      })}
    </g>
  );
}

function TierAndRibbon({ p }: { p: CardSvgProps }) {
  const right = ZONES.tier[0] + ZONES.tier[2];
  const maxW = ZONES.tier[2];
  const m = p.size === 'm';
  const items: ReactNode[] = [];
  const padX = 12;

  // Top plate: the state tag when there is one, else the preset ribbon (not on the medium card).
  const topText = p.stateTag ? (m ? (p.stateTagShort ?? p.stateTag) : p.stateTag) : m ? null : p.ribbon;
  if (topText && p.flags.badges) {
    const caps = topText.toLocaleUpperCase();
    const h = m ? 32 : 26;
    const fs = fitSize(caps, maxW - padX * 2, m ? 24 : 16, 10);
    const w = Math.min(maxW, textWidth(caps, fs) + padX * 2);
    items.push(
      <Plate
        key="top"
        x={right - w}
        y={ZONES.tier[1]}
        w={w}
        h={h}
        fill={p.stateTag ? p.stateFill : INK}
        stroke={p.stateTag ? 4 : 3}
        className={p.animate ? 'tts-cards-pop' : undefined}
      >
        <T
          x={right - w / 2}
          y={ZONES.tier[1] + h / 2 + (fs * CAP) / 2}
          size={fs}
          anchor="middle"
          fill={p.stateTag ? INK : WHITE}
        >
          {caps}
        </T>
      </Plate>,
    );
  }

  if (p.flags.tierTag) {
    const lines = p.tier ? [p.tierTag] : p.tierTag.split(' ');
    const caps = lines.map((l) => l.toLocaleUpperCase());
    const h = lines.length > 1 ? 46 : 38;
    const maxFs = m ? 26 : 22;
    const fs = Math.min(...caps.map((l) => fitSize(l, maxW - padX * 2, lines.length > 1 ? maxFs - 6 : maxFs, 10)));
    const w = Math.min(maxW, Math.max(...caps.map((l) => textWidth(l, fs))) + padX * 2);
    const y = ZONES.tier[1] + ZONES.tier[3] - h;
    const fill = windowFill(p.tier);
    items.push(
      <Plate
        key="tier"
        x={right - w}
        y={y}
        w={w}
        h={h}
        fill={fill}
        stroke={4}
        className={p.flags.stamp && p.animate ? 'tts-cards-stamp' : undefined}
      >
        {caps.map((l, i) => {
          const lineH = h / caps.length;
          return (
            <T key={l} x={right - w / 2} y={y + lineH * i + lineH / 2 + (fs * CAP) / 2} size={fs} anchor="middle">
              {l}
            </T>
          );
        })}
      </Plate>,
    );
  }
  return <g>{items}</g>;
}

/** The nameplate: skewed plate, name, subline and the jersey chip. */
function Nameplate({ p, uid }: { p: CardSvgProps; uid: string }) {
  const [px, py, pw, ph] = ZONES.nameplate;
  const m = p.size === 'm';
  const chipW = 58;
  const hasChip = !!p.jersey && p.flags.badges && !m;
  const inner = pw - 2 * 22 - (hasChip ? chipW + 10 : 0);
  const name = fitName(p.name, inner, m ? 50 : 34, m ? 30 : 22);
  const showSub = !m && p.flags.nameSub && p.subline.length > 0;
  const nameBase = showSub ? py + 31 : py + ph / 2 + (name.size * CAP) / 2 + 1;
  const subSize = fitSize(p.subline, inner, 15, 11);
  const clipId = `${uid}-plate`;
  return (
    <g>
      <defs>
        <clipPath id={clipId}>
          <rect x={px + 14} y={py} width={pw - 28} height={ph} />
        </clipPath>
      </defs>
      <Plate x={px} y={py} w={pw} h={ph} fill={windowFill(p.tier)} stroke={5} />
      <g clipPath={`url(#${clipId})`}>
        <T x={px + 24} y={nameBase} size={name.size}>
          {name.text}
        </T>
        {showSub && (
          <T x={px + 24} y={py + 54} size={subSize} fill={INK_SOFT} weight={700} italic={false}>
            {p.subline}
          </T>
        )}
      </g>
      {hasChip && p.jersey && (
        <g>
          <rect
            x={px + pw - 22 - chipW}
            y={py + 14}
            width={chipW}
            height={ph - 28}
            rx={3}
            fill={INK}
            transform={`translate(${px + pw - 22 - chipW / 2} ${py + ph / 2}) skewX(${SKEW}) translate(${-(px + pw - 22 - chipW / 2)} ${-(py + ph / 2)})`}
          />
          <T x={px + pw - 22 - chipW / 2} y={py + ph / 2 + (24 * CAP) / 2} size={24} anchor="middle" fill={WHITE}>
            {p.jersey}
          </T>
        </g>
      )}
    </g>
  );
}

/** The card body shadow: grape rect, or the four stacked copies for Myth. */
function BodyShadow({ tier, shadowColor }: { tier: TierId | null; shadowColor: string }) {
  const b = CARD_BODY;
  if (tier === 'myth') {
    const stack = ['#ffc299', '#ffffa1', '#92ffa7', '#97f0ff'];
    return (
      <g>
        {stack.map((fill, i) => {
          const off = CARD_SHADOW.dx - i * 4;
          return (
            <rect
              key={fill}
              x={b.x + off}
              y={b.y + off}
              width={b.w}
              height={b.h}
              rx={b.rx}
              fill={fill}
              stroke={INK}
              strokeWidth={2}
              strokeLinejoin="round"
            />
          );
        })}
      </g>
    );
  }
  return (
    <rect
      x={b.x + CARD_SHADOW.dx}
      y={b.y + CARD_SHADOW.dy}
      width={b.w}
      height={b.h}
      rx={b.rx}
      style={{ fill: shadowColor }}
    />
  );
}

function Figure({ p, uid }: { p: CardSvgProps; uid: string }) {
  const { figure, flags, shadowColor } = p;
  if (flags.figure === 'hidden') return null;
  const silhouette = flags.figure === 'silhouette';
  const nudge = p.nudge ?? null;
  if (figure.kind === 'pending' || figure.kind === 'window') return null;
  const layout = figure.kind === 'sticker' ? figure.layout : MANNEQUIN_LAYOUT;
  const pl = fitFigure(layout, nudge);
  const feetRx = Math.min(FEET_SHADOW.rxMax, Math.max(FEET_SHADOW.rxMin, FEET_SHADOW.rxFactor * pl.box.w));
  return (
    <g>
      <ellipse
        cx={ZONES.nameplate[0] + ZONES.nameplate[2] / 2}
        cy={FEET_SHADOW.cy + (nudge?.dy ?? 0)}
        rx={round(feetRx)}
        ry={FEET_SHADOW.ry}
        style={{ fill: shadowColor }}
      />
      <g clipPath={`url(#${uid}-pop)`}>
        {figure.kind === 'sticker' ? (
          <image
            href={figure.href}
            x={round(pl.image.x)}
            y={round(pl.image.y)}
            width={round(pl.image.w)}
            height={round(pl.image.h)}
            preserveAspectRatio="none"
            filter={silhouette ? `url(#${uid}-ink)` : undefined}
            className={p.animate ? 'tts-cards-fade' : undefined}
          />
        ) : (
          <g transform={`translate(${round(pl.image.x)} ${round(pl.image.y)}) scale(${round(pl.scale * 1000) / 1000})`}>
            <Mannequin
              silhouette={silhouette}
              accent={windowFill(p.tier) === WHITE ? '#e2a4ff' : windowFill(p.tier)}
              shadowColor={shadowColor}
            />
          </g>
        )}
      </g>
    </g>
  );
}

/** The whole card face. */
export function CardSvg(props: CardSvgProps) {
  const p = props;
  const { uid, size, tier, flags, shadowColor, animate } = p;
  const b = CARD_BODY;
  const win = ART_WINDOW;
  const fill = windowFill(tier);
  const unrated = tier === null;
  const centerX = ZONES.nameplate[0] + ZONES.nameplate[2] / 2;
  const numText = p.shownOvr === null ? '??' : String(p.shownOvr);
  const numSize = numText.length > 2 ? GIANT_NUMBER.size * 0.86 : GIANT_NUMBER.size;
  const [ox, oy, ow] = ZONES.ovr;
  const windowArt = p.figure.kind === 'window' && flags.figure !== 'hidden' ? p.figure.href : null;
  // Shape plus its hard shadow span `box` and are centered on the card middle; the bottom of the
  // shadow (and the Myth stack) lands 8 units inside the body's lower edge.
  const chipX = centerX - CHIP_BADGE_BOX / 2;
  const chipY = CARD_BODY.y + CARD_BODY.h - 8 - CHIP_BADGE_BOX;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 500 700"
      role="img"
      aria-label={p.title}
      data-testid="player-card-svg"
      data-size={size}
      style={{ display: 'block', width: '100%', height: 'auto', overflow: 'visible' }}
    >
      <defs>
        <clipPath id={`${uid}-win`}>
          <rect x={win.x} y={win.y} width={win.w} height={win.h} />
        </clipPath>
        <clipPath id={`${uid}-pop`}>
          <rect x={POPOUT_CLIP.x} y={POPOUT_CLIP.y} width={POPOUT_CLIP.w} height={POPOUT_CLIP.h} />
        </clipPath>
        <filter id={`${uid}-ink`} colorInterpolationFilters="sRGB">
          <feColorMatrix type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 1 0" />
        </filter>
      </defs>

      <BodyShadow tier={tier} shadowColor={shadowColor} />
      <rect
        x={b.x}
        y={b.y}
        width={b.w}
        height={b.h}
        rx={b.rx}
        fill={WHITE}
        stroke={INK}
        strokeWidth={b.stroke}
        strokeLinejoin="round"
        strokeDasharray={unrated ? '30 14' : undefined}
      />

      <g clipPath={`url(#${uid}-win)`}>
        <rect x={win.x} y={win.y} width={win.w} height={win.h} fill={fill} />
        {windowArt ? (
          <image
            href={windowArt}
            x={win.x}
            y={win.y}
            width={win.w}
            height={win.h}
            preserveAspectRatio={WINDOW_ART_FIT}
          />
        ) : (
          size !== 's' && <Backdrop preset={p.preset} fill={fill} />
        )}
        {flags.giant && !windowArt && size !== 's' && (
          <g transform={numeralTransform(centerX, GIANT_NUMBER.baseline, numSize, GIANT_NUMBER.skewDeg)}>
            <T x={0} y={0} size={numSize} anchor="middle" fill={numeralFill(fill)} stroke={INK} strokeWidth={6}>
              {numText}
            </T>
          </g>
        )}
        {flags.stamp && animate && (
          <rect x={win.x} y={win.y} width={win.w} height={win.h} fill={fill} className="tts-cards-flash" />
        )}
      </g>
      <rect
        x={win.x}
        y={win.y}
        width={win.w}
        height={win.h}
        fill="none"
        stroke={INK}
        strokeWidth={win.stroke}
        strokeLinejoin="round"
        strokeDasharray={unrated ? '22 12' : undefined}
      />

      <Figure p={p} uid={uid} />

      {size === 's' ? (
        flags.ovrShape && (
          <OvrBadge
            tier={tier}
            value={flags.ovrNumber === 'zero' ? 0 : p.shownOvr}
            x={chipX}
            y={chipY}
            box={CHIP_BADGE_BOX}
            shadowColor={shadowColor}
            animate={animate}
            stamp={flags.stamp}
            chip
          />
        )
      ) : (
        <>
          {flags.ovrShape && (
            <OvrBadge
              tier={tier}
              value={flags.ovrNumber === 'zero' ? 0 : p.shownOvr}
              x={ox}
              y={oy}
              box={ow}
              shadowColor={shadowColor}
              animate={animate}
              stamp={flags.stamp}
            />
          )}
          <TierAndRibbon p={p} />
          {flags.name && <Nameplate p={p} uid={uid} />}
          {flags.attrs && p.attrs.length > 0 && (
            <AttrBlock
              attrs={p.attrs}
              size={size === 'm' ? 'm' : 'l'}
              tier={tier}
              animate={animate}
              singleDigitTitle={p.singleDigitTitle}
            />
          )}
        </>
      )}
    </svg>
  );
}

/** Slant of the skew in px per px of height, for tests that check the numeral stays centered. */
export const SKEW_TAN = TAN;
